<template>
  <div v-if="intensity > 0" class="cherry-blossoms-overlay">
    <div
      v-for="(petal, index) in activePetals"
      :key="`petal-${index}`"
      class="cherry-petal"
      :style="getPetalStyle(petal)"
    >
      🌸
    </div>
  </div>
</template>

<script>
export default {
  name: 'CherryBlossoms',
  props: {
    intensity: {
      type: Number,
      default: 0,
      validator: value => value >= 0 && value <= 1314
    }
  },
  data () {
    return {
      activePetals: [],
      animationId: null,
      lastSpawnTime: 0,
      petalIdCounter: 0
    }
  },
  watch: {
    intensity: {
      handler (newVal) {
        if (newVal > 0) {
          this.startAnimation()
        } else {
          this.stopAnimation()
        }
      },
      immediate: true
    }
  },
  beforeDestroy () {
    this.stopAnimation()
  },
  methods: {
    startAnimation () {
      if (this.animationId) return

      this.animationLoop()
    },

    stopAnimation () {
      if (this.animationId) {
        cancelAnimationFrame(this.animationId)
        this.animationId = null
      }
      this.activePetals = []
    },

    animationLoop () {
      const now = Date.now()

      // 根据强度控制生成频率
      const spawnInterval = this.getSpawnInterval()
      if (now - this.lastSpawnTime > spawnInterval) {
        this.spawnPetals()
        this.lastSpawnTime = now
      }

      // 更新现有花瓣位置
      this.updatePetals()

      // 清理超出屏幕的花瓣
      this.cleanupPetals()

      this.animationId = requestAnimationFrame(() => this.animationLoop())
    },

    getSpawnInterval () {
      // 强度越高，生成间隔越短
      if (this.intensity <= 10) return 2000 - this.intensity * 100
      if (this.intensity <= 50) return 1000 - (this.intensity - 10) * 20
      if (this.intensity <= 200) return 200 - (this.intensity - 50) * 1
      return 50 // 最小间隔50ms
    },

    getSpawnCount () {
      // 根据强度决定每次生成的花瓣数量
      if (this.intensity <= 10) return 1
      if (this.intensity <= 50) return Math.ceil(this.intensity / 10)
      if (this.intensity <= 200) return Math.ceil(this.intensity / 20)
      return Math.ceil(this.intensity / 50) // 高强度时控制数量避免卡顿
    },

    spawnPetals () {
      const count = this.getSpawnCount()
      const maxPetals = Math.min(this.intensity * 2, 200) // 限制最大花瓣数量

      if (this.activePetals.length >= maxPetals) return

      for (let i = 0; i < count; i++) {
        if (this.activePetals.length >= maxPetals) break

        this.activePetals.push({
          id: this.petalIdCounter++,
          x: Math.random() * window.innerWidth,
          y: -20,
          speedY: 1 + Math.random() * 2,
          speedX: (Math.random() - 0.5) * 0.5,
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 2,
          opacity: 0.7 + Math.random() * 0.3,
          scale: 0.8 + Math.random() * 0.4,
          createdAt: Date.now()
        })
      }
    },

    updatePetals () {
      this.activePetals.forEach(petal => {
        petal.y += petal.speedY
        petal.x += petal.speedX
        petal.rotation += petal.rotationSpeed

        // 添加轻微的摆动效果
        petal.x += Math.sin(Date.now() * 0.001 + petal.id) * 0.2
      })
    },

    cleanupPetals () {
      this.activePetals = this.activePetals.filter(petal => {
        return petal.y < window.innerHeight + 50 &&
               petal.x > -50 &&
               petal.x < window.innerWidth + 50
      })
    },

    getPetalStyle (petal) {
      return {
        position: 'absolute',
        left: petal.x + 'px',
        top: petal.y + 'px',
        transform: `rotate(${petal.rotation}deg) scale(${petal.scale})`,
        opacity: petal.opacity,
        fontSize: '16px',
        pointerEvents: 'none',
        zIndex: 10,
        transition: 'none'
      }
    }
  }
}
</script>

<style scoped>
.cherry-blossoms-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 10;
  overflow: hidden;
}

.cherry-petal {
  will-change: transform, opacity;
  transform: translateZ(0);
  backface-visibility: hidden;
}

/* 高强度时的特殊效果 */
@media (max-width: 600px) {
  .cherry-petal {
    font-size: 14px; /* 移动端稍小一些 */
  }
}
</style>
